import { Button, Typography } from '@/components/atoms';
import type { TypographyProps, TypographyVariant } from '@/components/atoms/Typography';
import { UI_PRIMITIVES } from '@/core/constants/uiColors';
import { useUIColors, useWebViewport } from '@/core/hooks';
import { useI18nContext } from '@/core/providers/I18nProvider';
import React from 'react';
import {
  Modal,
  Platform,
  Pressable,
  StyleSheet,
  View,
  type StyleProp,
  type TextStyle,
  type ViewStyle,
} from 'react-native';

const DIALOG_EDGE_INSET = 32;

export type ContentDialogActionVariant = 'primary' | 'outline' | 'text';

export interface ContentDialogAction {
  label: string;
  onPress: () => void;
  variant?: ContentDialogActionVariant;
  disabled?: boolean;
  /** Stretch equally in a row when multiple actions */
  flex?: boolean;
  /** Override Button fullWidth (defaults to true when flexing / single action) */
  fullWidth?: boolean;
  /** Optional override for button container */
  style?: StyleProp<ViewStyle>;
  /** Optional override for button label */
  textStyle?: StyleProp<TextStyle>;
}

export interface ContentDialogProps {
  visible: boolean;
  onClose: () => void;
  /** Optional leading/hero visual (icon, illustration) */
  icon?: React.ReactNode;
  title: string;
  subtitle?: string;
  titleVariant?: TypographyVariant;
  titleColor?: TypographyProps['color'];
  subtitleVariant?: TypographyVariant;
  subtitleColor?: TypographyProps['color'];
  /** Optional style on the subtitle */
  subtitleStyle?: StyleProp<TextStyle>;
  /** Optional style on the title */
  titleStyle?: StyleProp<TextStyle>;
  /** Main body — fully custom content */
  children?: React.ReactNode;
  /** Footer actions (1–2 typical). Empty = no footer. */
  actions?: ContentDialogAction[];
  /** Center title/subtitle/icon (success / confirm dialogs) */
  contentAlign?: 'left' | 'center';
  /** Max width of the card */
  maxWidth?: number;
  /** Optional style on the card container */
  cardStyle?: StyleProp<ViewStyle>;
  /** Optional style on header (title/subtitle stack) — e.g. larger title↔subtitle gap */
  headerStyle?: StyleProp<ViewStyle>;
  /** Optional style on the actions row */
  actionsStyle?: StyleProp<ViewStyle>;
  /** Close when pressing the dimmed backdrop (default true) */
  closeOnBackdropPress?: boolean;
}

/**
 * Config-driven modal shell: icon + title + subtitle + custom body + action row.
 * Feature modules pass content; this molecule only handles layout/chrome.
 */
export function ContentDialog({
  visible,
  onClose,
  icon,
  title,
  subtitle,
  titleVariant = 'title2',
  titleColor = 'primary',
  subtitleVariant = 'body',
  subtitleColor = 'secondary',
  subtitleStyle,
  titleStyle,
  children,
  actions = [],
  contentAlign = 'left',
  maxWidth = 520,
  cardStyle,
  headerStyle,
  actionsStyle,
  closeOnBackdropPress = true,
}: Readonly<ContentDialogProps>) {
  const colors = useUIColors();
  const { isRTL } = useI18nContext();
  const { breakpointHeight } = useWebViewport();
  const centered = contentAlign === 'center';
  /** Cap only — hug content; scroll/overflow only when past the viewport. */
  const cardMaxHeight =
    Platform.OS === 'web'
      ? Math.max(200, breakpointHeight - DIALOG_EDGE_INSET)
      : ('100%' as const);

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={closeOnBackdropPress ? onClose : undefined}
    >
      <View
        style={[
          styles.overlay,
          { backgroundColor: UI_PRIMITIVES.overlayBackdrop },
          Platform.OS === 'web'
            ? ({ 
                maxHeight: '100%', 
                overflowY: 'auto',
                direction: isRTL ? 'rtl' : 'ltr'
              } as ViewStyle)
            : null,
        ]}
      >
        {closeOnBackdropPress ? (
          <Pressable
            style={styles.backdrop}
            onPress={onClose}
            accessibilityRole="button"
            accessibilityLabel="Close dialog"
          />
        ) : null}
        <View
          style={[
            styles.card,
            {
              backgroundColor: colors.backgroundLevel1,
              maxWidth,
              maxHeight: cardMaxHeight,
            },
            cardStyle,
          ]}
        >
          <ContentDialogHeader
            centered={centered}
            isRTL={isRTL}
            icon={icon}
            title={title}
            titleVariant={titleVariant}
            titleColor={titleColor}
            titleStyle={titleStyle}
            subtitle={subtitle}
            subtitleVariant={subtitleVariant}
            subtitleColor={subtitleColor}
            subtitleStyle={subtitleStyle}
            headerStyle={headerStyle}
          />

          {children ? <View style={styles.body}>{children}</View> : null}

          {actions.length > 0 ? (
            <ContentDialogActions
              actions={actions}
              centered={centered}
              actionsStyle={actionsStyle}
            />
          ) : null}
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 16,
  },
  /** Sibling behind the card so web does not nest <button> inside backdrop Pressable. */
  backdrop: {
    ...StyleSheet.absoluteFillObject,
  },
  card: {
    width: '100%',
    borderRadius: 24,
    paddingTop: 32,
    paddingBottom: 24,
    paddingHorizontal: 24,
    gap: 24,
    overflow: 'hidden',
    zIndex: 1,
  },
  header: {
    gap: 8,
    alignItems: 'flex-start',
    flexShrink: 0,
  },
  headerCentered: {
    alignItems: 'center',
  },
  iconWrap: {
    marginBottom: 8,
  },
  iconWrapCentered: {
    marginBottom: 0,
  },
  titleLine: {
    lineHeight: 22,
  },
  subtitleLine: {
    lineHeight: 16,
  },
  textCenter: {
    textAlign: 'center',
    width: '100%',
  },
  body: {
    width: '100%',
    flexGrow: 0,
    flexShrink: 1,
    minHeight: 0,
  },
  actions: {
    flexDirection: 'row',
    gap: 12,
    width: '100%',
    flexShrink: 0,
  },
  actionsCentered: {
    justifyContent: 'center',
  },
  actionsSingle: {
    flexDirection: 'column',
  },
  actionFlex: {
    flex: 1,
  },
  actionFull: {
    width: '100%',
  },
});

function ContentDialogHeader({
  centered,
  isRTL,
  icon,
  title,
  titleVariant,
  titleColor,
  titleStyle,
  subtitle,
  subtitleVariant,
  subtitleColor,
  subtitleStyle,
  headerStyle,
}: any) {
  return (
    <View
      style={[
        styles.header,
        centered && styles.headerCentered,
        Platform.OS === 'web' && isRTL && !centered ? { alignItems: 'flex-end' } as ViewStyle : null,
        headerStyle,
      ]}
    >
      {icon ? (
        <View style={[styles.iconWrap, centered && styles.iconWrapCentered]}>
          {icon}
        </View>
      ) : null}
      <Typography
        variant={titleVariant}
        color={titleColor}
        style={[
          styles.titleLine,
          centered && styles.textCenter,
          Platform.OS === 'web' && isRTL && !centered ? { textAlign: 'right', width: '100%' } as TextStyle : null,
          titleStyle
        ]}
      >
        {title}
      </Typography>
      {subtitle ? (
        <Typography
          variant={subtitleVariant}
          color={subtitleColor}
          style={[
            styles.subtitleLine,
            centered && styles.textCenter,
            Platform.OS === 'web' && isRTL && !centered ? { textAlign: 'right', width: '100%' } as TextStyle : null,
            subtitleStyle,
          ]}
        >
          {subtitle}
        </Typography>
      ) : null}
    </View>
  );
}

function ContentDialogActions({ actions, centered, actionsStyle }: any) {
  return (
    <View
      style={[
        styles.actions,
        actions.length === 1 && styles.actionsSingle,
        centered && styles.actionsCentered,
        actionsStyle,
      ]}
    >
      {actions.map((action: any) => {
        let wrapStyle: typeof styles.actionFlex | typeof styles.actionFull | undefined;
        if (actions.length > 1) {
          wrapStyle = action.flex !== false ? styles.actionFlex : undefined;
        } else {
          wrapStyle = styles.actionFull;
        }

        return (
          <View key={action.label} style={wrapStyle}>
            <Button
              title={action.label}
              variant={action.variant ?? 'primary'}
              fullWidth={action.fullWidth ?? true}
              {...(action.disabled !== undefined
                ? { disabled: action.disabled }
                : {})}
              {...(action.style !== undefined
                ? { style: action.style }
                : {})}
              onPress={action.onPress}
              {...(action.textStyle
                ? { textStyle: action.textStyle }
                : {})}
            />
          </View>
        );
      })}
    </View>
  );
}
export default ContentDialog;
